// --- Core
import { DL_LB_DB, IR, QB_RB_WR_TE, TAXI } from '../helpers/RosterSpotHelper.js';

/**
 * Displays a player position or roster spot. Accepts a default slot that will
 * render the text inside of the element containing the position or roster spot
 * which is useful for including a positional rank.
 *
 * @element pos-roster-spot
 * @attribute {string} pos-roster-spot A fantasy position / roster spot string
 * @attribute {boolean} pill Whether to render with pill styles (reactive: toggling the attribute re-renders)
 *
 * Usage:
 *
 * ```html
 * <pos-roster-spot pos-roster-spot="RB|WR|TE" pill> </pos-roster-spot>
 * ```
 *
 * Default slot content is cloned into each position segment.
 */
export class PosRosterSpotElement extends HTMLElement {
    /**
     * @type {boolean}
     */
    _renderPending = false;

    /**
     *  @type {DocumentFragment|null}
     */
    _slotFrag = null;

    /**
     * Watches the original slot nodes, which the consumer that rendered this element may keep
     * patching by reference (e.g. a Vue template updating a rank). The rendered position segments
     * contain clones of these nodes, so they must be refreshed when the source changes.
     * @type {MutationObserver|null}
     */
    _slotObserver = null;

    static get observedAttributes() {
        return ['pos-roster-spot', 'pill'];
    }

    connectedCallback() {
        this._observeSlot();
        this._scheduleRender();
    }

    disconnectedCallback() {
        this._slotObserver?.disconnect();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue || !this.isConnected) {
            return;
        }
        this._scheduleRender();
    }

    /**
     * Coalesces multiple synchronous attribute changes into a single render
     */
    _scheduleRender() {
        if (this._renderPending) {
            return;
        }
        this._renderPending = true;
        queueMicrotask(() => {
            this._renderPending = false;
            if (!this.isConnected) {
                return;
            }
            this.render();
        });
    }

    /**
     * Observe reactive updates to the source slot nodes kept in `_slotFrag`.
     */
    _observeSlot() {
        // No slot content, so no need to observe for changes
        if (this._slotFrag === null) {
            return;
        }
        // Only create the observer once, since it will be reused for the lifetime of this element
        if (this._slotObserver === null) {
            this._slotObserver = new MutationObserver(() => this._scheduleRender());
        }
        // Observe the source slot nodes for changes, and re-render when they change
        this._slotObserver.observe(this._slotFrag, {
            characterData: true,
            childList: true,
            subtree: true,
        });
    }

    /**
     * The position or roster spot that should be displayed
     */
    get posRosterSpot() {
        return this.getAttribute('pos-roster-spot') ?? '';
    }
    set posRosterSpot(value) {
        this.setAttribute('pos-roster-spot', value);
    }

    /**
     * Whether to render with pill styles
     */
    get pill() {
        if (!this.hasAttribute('pill')) {
            return true;
        }
        const raw = this.getAttribute('pill');
        return raw !== 'false' && raw !== '0';
    }
    set pill(value) {
        if (!value || value === 'false' || value === '0') {
            this.setAttribute('pill', 'false');
            return;
        }
        this.setAttribute('pill', '');
    }

    /**
     * Renders the position or roster spot
     */
    render() {
        if (this._slotFrag === null) {
            this._slotFrag = document.createDocumentFragment();
            while (this.firstChild) {
                this._slotFrag.appendChild(this.firstChild);
            }
            this._observeSlot();
        }

        const parts = computeParts(this.posRosterSpot, this.pill);

        this.classList.remove(
            'pos-roster-spot--1-items',
            'pos-roster-spot--2-items',
            'pos-roster-spot--3-items',
            'pos-roster-spot--4-items',
            'pos-roster-spot--pill'
        );
        this.classList.add('pos-roster-spot', `pos-roster-spot--${parts.length}-items`);
        if (this.pill) {
            this.classList.add('pos-roster-spot--pill');
        }

        const slotCloneSource = this._slotFrag;
        const children = [];
        for (const part of parts) {
            const divEl = document.createElement('div');
            divEl.className = part.class.toLowerCase();
            divEl.append(document.createTextNode(part.text));
            divEl.appendChild(slotCloneSource.cloneNode(true));
            children.push(divEl);
        }

        this.replaceChildren(...children);
    }
}

/**
 * @param {string} partText
 * @param {boolean} pill
 * @returns {string}
 */
function getPartClass(partText, pill) {
    if (!pill) {
        return '';
    }
    // prettier-ignore
    return [IR, TAXI].includes(partText)
        ? 'outline-' + partText.toLowerCase()
        : 'bg-' + partText.toLowerCase();
}

/**
 * @param {string} posRosterSpot
 * @param {boolean} pill
 * @returns {Array<{ text: string, class: string }>}
 */
function computeParts(posRosterSpot, pill) {
    const segments = posRosterSpot.split('|');

    if (segments.length === 1) {
        const displayText = segments[0] === 'TAXI' ? 'TX' : segments[0];
        let classAttr =
            segments[0] === 'TAXI' ? 'outline-ds-orange' : segments[0] === 'IR' ? 'outline-ds-red' : getPartClass(segments[0], pill);
        return [{ text: displayText, class: classAttr }];
    }

    if (posRosterSpot === DL_LB_DB) {
        return [{ text: 'IDP', class: getPartClass('idp', pill) }];
    }

    // prettier-ignore
    const partsInOrder =
        posRosterSpot === QB_RB_WR_TE ? ['R', 'W', 'T', 'Q'] : posRosterSpot.split('|');

    const positionParts = [];
    for (let i = 0; i < partsInOrder.length; i++) {
        const text = partsInOrder[i];
        let classStr = partsInOrder[i].toLowerCase();
        // prettier-ignore
        switch (text) {
            case 'R':
                classStr = getPartClass('rb', pill);
                break;
            case 'W':
                classStr = getPartClass('wr', pill);
                break;
            case 'T':
                classStr = getPartClass('te', pill);
                break;
            case 'Q':
                classStr = getPartClass('qb', pill);
                break;
        }
        positionParts.push({
            text,
            class: classStr,
        });
    }
    return positionParts;
}

const tagName = 'pos-roster-spot';

if (typeof customElements !== 'undefined' && !customElements.get(tagName)) {
    customElements.define(tagName, PosRosterSpotElement);
}
